import { ApiMethod } from '@wajeez-web/stores/base';
import { Server, contentClient } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { thunkErrorHandler } from '@wajeez-web/core/utils';

export const getCollectionComponentTypeByName: ApiMethod<
  string,
  Server.CollectionComponentTypeOutput,
  any
> = async (name, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );
    const response = await client.collectionComponentTypes_byName_get_V1(
      undefined,
      name
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getCollectionComponentTypeById: ApiMethod<
  number,
  Server.CollectionComponentTypeOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponentTypes_byId_get_V1(undefined, id);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentTypeByIdMultilingual: ApiMethod<
  number,
  Server.CollectionComponentTypeMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponentTypes_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_CollectionComponentTypes_multilingualById_get_V1(id);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getCollectionComponentTypesDatatable: ApiMethod<
  Server.CollectionComponentTypesLookUpDatatable,
  Server.TableOfCollectionComponentTypesLookUpColumns,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponentTypes_lookups_datatable_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const addAllowedType: ApiMethod<
  Server.AddAllowedComponentTypeInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponentTypes_addAllowedType_post_V1(data);

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getCollectionComponentTypesLookups: ApiMethod<
  void,
  Server.CollectionComponentTypesLookUpOutput[],
  void
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponentTypes_lookups_get_V1();

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createCollectionComponentType: ApiMethod<
  Server.CreateCollectionComponentTypeInput,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response = await client.collectionComponentTypes_post_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createCollectionComponentTypeMultilingual: ApiMethod<
  Server.CreateCollectionComponentTypeV2Input,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponentTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_CollectionComponentTypes_post_V2(
        data
      );

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editCollectionComponentTypeMultilingual: ApiMethod<
  Server.UpdateCollectionComponentTypeInput,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_CollectionComponentTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_CollectionComponentTypes_put_V1(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const setAllowedComponentTypes: ApiMethod<
  Server.SetAllowedComponentTypesInput,
  void,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_CollectionComponentTypes_Client(
      undefined,
      contentClient
    );

    const response =
      await client.collectionComponentTypes_setAllowedComponentTypes_post_V1(
        data
      );

    if (response.success) {
      return;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};
